import type { HTMLAttributes, ReactNode } from 'react';

import { clsx } from 'clsx';

type CommonTypes = {
    children: ReactNode;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    className?: string;
};

export type VariantProps =
    | {
          variant: 'normal';
          color: 'orange' | 'blue' | 'violet' | 'rose' | 'indigo' | 'gray' | 'green';
      }
    | {
          variant: 'colorful';
          color: 'yellow' | 'blue' | 'green' | 'red' | 'gray' | 'orange' | 'rose' | 'indigo' | 'white';
      };

export type ChipStatusProps = CommonTypes & VariantProps;

export const Chip = ({ variant, color, children, dataTestid, htmlAttributes, className }: ChipStatusProps) => (
    <div
        className={clsx(
            // Common styles
            [
                'text-xs',
                'font-normal',
                'rounded-full',
                'inline-block',
                'py-1',
                'px-2',
                'whitespace-nowrap',
                'font-["Inter"]',
            ],
            // Colorful variants
            {
                'bg-amber-100 text-amber-700': color === 'yellow' && variant === 'colorful',
                'bg-blue-100 text-blue-700': color === 'blue' && variant === 'colorful',
                'bg-emerald-100 text-emerald-700': color === 'green' && variant === 'colorful',
                'bg-red-100 text-red-700': color === 'red' && variant === 'colorful',
                'bg-gray-200 text-gray-700': color === 'gray' && variant === 'colorful',
                'bg-orange-100 text-orange-700': color === 'orange' && variant === 'colorful',
                'bg-rose-100 text-rose-700': color === 'rose' && variant === 'colorful',
                'bg-indigo-100 text-indigo-700': color === 'indigo' && variant === 'colorful',
                'bg-white text-gray-700': color === 'white' && variant === 'colorful',
            },
            // Normal variants
            {
                'bg-orange-100 text-gray-800': color === 'orange' && variant === 'normal',
                'bg-sky-100 text-gray-800': color === 'blue' && variant === 'normal',
                'bg-violet-100 text-gray-800': color === 'violet' && variant === 'normal',
                'bg-rose-100 text-gray-800': color === 'rose' && variant === 'normal',
                'bg-indigo-100 text-gray-800': color === 'indigo' && variant === 'normal',
                'bg-gray-100 text-gray-700': color === 'gray' && variant === 'normal',
                'bg-emerald-100 text-gray-800': color === 'green' && variant === 'normal',
            },
            className
        )}
        data-testid={dataTestid}
        data-component-name='Chip'
        {...htmlAttributes}
    >
        {children}
    </div>
);
